<script setup lang="ts">
import Dialog from 'primevue/dialog';
import InputOtp from 'primevue/inputotp';
import { computed, ref } from 'vue';
import CustomButton from '../common/CustomButton.vue';
import { ButtonType } from '../common';
import { LocalStorageUtils } from '@/utils';
import { useStudyStore } from '@/stores/study';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const studyStore = useStudyStore();
const existingPinInput = ref<string>('');
const enteredPin = ref<string>('');
const confirmPin = ref<string>('');
const showWrongExistingPinError = ref(false);
const showMismatchPinError = ref(false);

const emits = defineEmits(['onClose']);

const closeDialog = async () => {
  emits('onClose');
};

const onSave = async () => {
  if (existingPin.value && existingPinInput.value !== existingPin.value) {
    showWrongExistingPinError.value = true;
    return;
  }

  if (enteredPin.value !== confirmPin.value) {
    showMismatchPinError.value = true;
    return;
  }

  LocalStorageUtils.setPart11Pin(enteredPin.value);
  studyStore.studyLoader.setSuccess('PIN saved successfully!');
  closeDialog();
};

const existingPin = computed(() => {
  return LocalStorageUtils.getPart11Pin();
});

const enableSaveButton = computed(() => {
  if (existingPin.value) {
    return existingPin.value.length === 4 && enteredPin.value.length === 4 && confirmPin.value.length === 4;
  }
  return enteredPin.value.length === 4 && confirmPin.value.length === 4;
});
</script>

<template>
  <Dialog :visible="visible"
    modal
    header="Setup PIN for 21 CFR Part 11 Compliance"
    class="dialog"
    style="width: 500px"
    :closeOnEscape="true"
    :draggable="false"
    @update:visible="closeDialog">
    <div class="pin-container" v-if="existingPin">
      <div class="pin-label">Current PIN</div>
      <InputOtp v-model="existingPinInput" mask />
      <div class="pin-error" v-if="showWrongExistingPinError">
        Incorrect PIN!
      </div>
    </div>

    <div class="pin-container">
      <div class="pin-label">New PIN</div>
      <InputOtp v-model="enteredPin" mask />
    </div>

    <div class="pin-container">
      <div class="pin-label">Confirm PIN</div>
      <InputOtp v-model="confirmPin" mask />
      <div class="pin-error" v-if="showMismatchPinError">
        PIN does not match!
      </div>
    </div>

    <div class="buttons">
      <CustomButton text="Cancel"
        :type="ButtonType.WITHOUT_OUTLINE"
        @onClick="closeDialog"/>
      <CustomButton text="Save"
        :type="ButtonType.POSITIVE"
        @onClick="onSave"
        :disabled="!enableSaveButton"/>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
.pin-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  .pin-error {
    color: red;
    font-size: 14px;
    margin-top: 8px;
  }
}
.pin-label {
  font-weight: bold;
  margin-bottom: 8px;
}
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
</style>
